/**
 * Section — labelled container with title, optional eyebrow, description, and action slot.
 *
 * Single responsibility: render the standard heading block above an arbitrary
 * children region. Composes naturally with Card for content surfaces.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { FONT, GRAY } from "../design-system/tokens.js";

const SECTION_STYLE: React.CSSProperties = {
  marginTop: "1.5rem",
  contentVisibility: "auto",
  containIntrinsicSize: "auto 320px",
};

const SECTION_HEADER_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
  gap: "1rem",
  marginBottom: "0.75rem",
};

const SECTION_TITLE_BLOCK_STYLE: React.CSSProperties = { flex: 1, minWidth: 0 };

const SECTION_EYEBROW_STYLE: React.CSSProperties = {
  fontSize: "0.6875rem",
  fontWeight: 700,
  color: GRAY[500],
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  marginBottom: "0.25rem",
};

const SECTION_TITLE_STYLE: React.CSSProperties = {
  fontFamily: FONT.heading,
  fontSize: "1.125rem",
  fontWeight: 700,
  color: GRAY[900],
  margin: 0,
  lineHeight: 1.3,
};

const SECTION_DESCRIPTION_STYLE: React.CSSProperties = {
  color: GRAY[600],
  margin: "0.25rem 0 0",
  fontSize: "0.875rem",
};

const SECTION_ACTION_STYLE: React.CSSProperties = { flexShrink: 0 };

export interface SectionProps {
  /** Small uppercase label rendered above the title (e.g. "OVERVIEW"). */
  readonly eyebrow?: string;
  /** Visible section title. */
  readonly title: string;
  /** Optional description rendered below the title. */
  readonly description?: string;
  /** Right-aligned slot for actions (filters, links, toggles). */
  readonly action?: React.ReactNode;
  /** Section body. */
  readonly children?: React.ReactNode;
  /** Optional id for in-page anchoring. */
  readonly id?: string;
}

/** Renders a standardized section with heading and optional action slot. */
export const Section = React.memo(function Section({
  eyebrow,
  title,
  description,
  action,
  children,
  id,
}: SectionProps): React.JSX.Element {
  return (
    <section style={SECTION_STYLE} id={id} aria-labelledby={id ? `${id}-title` : undefined}>
      <header style={SECTION_HEADER_STYLE}>
        <div style={SECTION_TITLE_BLOCK_STYLE}>
          {eyebrow !== undefined && <div style={SECTION_EYEBROW_STYLE}>{eyebrow}</div>}
          <h2 id={id ? `${id}-title` : undefined} style={SECTION_TITLE_STYLE}>
            {title}
          </h2>
          {description !== undefined && <p style={SECTION_DESCRIPTION_STYLE}>{description}</p>}
        </div>
        {action !== undefined && <div style={SECTION_ACTION_STYLE}>{action}</div>}
      </header>
      {children}
    </section>
  );
});
